import { useState, useEffect } from 'react';
import { ClipboardCheck, Download, FileArchive, FileJson, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { ModuleResult } from '@/types/simulation-results';

const MODULE_LABELS: Record<string, string> = {
  reading: 'Reading',
  listening: 'Listening',
  writing: 'Writing',
  speaking: 'Speaking',
};

interface ExportPanelProps {
  readonly availableModules: ModuleResult[];
  readonly isExporting: boolean;
  readonly isHtmlExporting?: boolean;
  readonly onExport: (selectedModules: string[]) => void;
  readonly onExportJson?: (selectedModules: string[]) => void;
  readonly onExportHtml?: (selectedModules: string[], audience: 'full' | 'examiner') => void;
}

export function ExportPanel({
  availableModules,
  isExporting,
  isHtmlExporting = false,
  onExport,
  onExportJson,
  onExportHtml,
}: Readonly<ExportPanelProps>) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(availableModules.map((m) => m.module)),
  );
  const [active, setActive] = useState<'pdf' | 'json' | 'html' | 'html-examiner' | null>(null);

  useEffect(() => {
    if (!isExporting && !isHtmlExporting) setActive(null);
  }, [isExporting, isHtmlExporting]);

  const toggle = (mod: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(mod)) {
        next.delete(mod);
      } else {
        next.add(mod);
      }
      return next;
    });
  };

  const handlePdf = () => {
    setActive('pdf');
    onExport(Array.from(selected));
  };

  const handleJson = () => {
    if (!onExportJson) return;
    setActive('json');
    onExportJson(Array.from(selected));
  };

  const handleHtml = () => {
    if (!onExportHtml) return;
    setActive('html');
    onExportHtml(Array.from(selected), 'full');
  };

  const handleExaminerHtml = () => {
    if (!onExportHtml) return;
    setActive('html-examiner');
    onExportHtml(Array.from(selected), 'examiner');
  };

  const busy = isExporting || isHtmlExporting;
  const disabled = selected.size === 0 || busy;

  return (
    <div className="mt-2 rounded-md border border-border bg-muted/30 p-3">
      <p className="mb-2.5 font-ui text-xs font-medium text-foreground">
        Select modules to export:
      </p>
      <div className="mb-3 flex flex-wrap gap-x-5 gap-y-2">
        {availableModules.map((mod) => (
          <div key={mod.module} className="flex items-center gap-1.5">
            <Checkbox
              id={`export-mod-${mod.module}`}
              checked={selected.has(mod.module)}
              onCheckedChange={() => toggle(mod.module)}
              disabled={busy}
            />
            <Label
              htmlFor={`export-mod-${mod.module}`}
              className="cursor-pointer font-ui text-xs"
            >
              {MODULE_LABELS[mod.module] ?? mod.module}
            </Label>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={disabled}
          onClick={handlePdf}
          className="h-7 gap-1.5 text-xs"
        >
          {active === 'pdf' && isExporting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          {active === 'pdf' && isExporting ? 'Preparing…' : 'Download PDF'}
        </Button>
        {onExportJson && (
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={handleJson}
            className="h-7 gap-1.5 text-xs"
          >
            {active === 'json' && isExporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileJson className="h-3.5 w-3.5" />
            )}
            {active === 'json' && isExporting ? 'Preparing…' : 'Download JSON'}
          </Button>
        )}
        {onExportHtml && (
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={handleHtml}
            className="h-7 gap-1.5 text-xs"
          >
            {active === 'html' && isHtmlExporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileArchive className="h-3.5 w-3.5" />
            )}
            {active === 'html' && isHtmlExporting ? 'Preparing…' : 'Download HTML ZIP'}
          </Button>
        )}
        {onExportHtml && (
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={handleExaminerHtml}
            className="h-7 gap-1.5 text-xs"
            title="Blind copy for an examiner: no AI grades, scores, feedback, or expected answers"
          >
            {active === 'html-examiner' && isHtmlExporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ClipboardCheck className="h-3.5 w-3.5" />
            )}
            {active === 'html-examiner' && isHtmlExporting ? 'Preparing…' : 'Examiner HTML'}
          </Button>
        )}
      </div>
    </div>
  );
}
